import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthService } from "@/hooks/services/authService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { 
  Mail, 
  Lock, 
  User, 
  ArrowRight, 
  CheckCircle2, 
  AlertCircle, 
  Eye, 
  EyeOff 
} from "lucide-react";

const SignupPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("student");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    // Form Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError("Please fill in all fields");
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      setIsLoading(false);
      return;
    }

    if (!agreedToTerms) {
      setError("Please agree to the Terms and Conditions");
      setIsLoading(false);
      return;
    }

    // Actual API/Service Integration
    try {
      // Create user via AuthService
      const result = await AuthService.register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        role: activeTab, // Track whether they signed up as a student or school
      });

      // Automatically log the user in by updating the global AuthStore
      AuthStore.getState().login(true, activeTab, result.user);

      setSuccess(true);
      
      // Navigate to the correct onboarding/dashboard step after a brief success message
      setTimeout(() => {
        navigate(activeTab === "student" ? "/career-assessment" : "/school-dashboard");
      }, 1500);

    } catch (err) {
      setError(err.message || "Failed to create account. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Background decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 right-10 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl"></div>
      </div>

      {/* Layout container with side swap */}
      <div className="w-full flex flex-col lg:flex-row relative z-10">
        {/* Image Section */}
        <div
          className={`hidden lg:flex w-1/2 items-center justify-center p-8 transition-all duration-500 ${
            activeTab === "school" ? "order-2" : "order-1"
          }`}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {activeTab === "student" && (
              <div className="flex flex-col items-center justify-center space-y-6 max-w-lg">
                <div className="relative w-full max-w-md">
                  <img
                    src="/students.avif"
                    alt="Team Player"
                    className="w-full h-auto rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-blue-700 text-white px-6 py-2 rounded-full font-bold text-lg shadow-lg">
                    Student Journey
                  </div>
                </div>
              </div>
            )}
            {activeTab === "school" && (
              <div className="flex flex-col items-center justify-center space-y-6 max-w-lg">
                <div className="relative w-full max-w-md">
                  <img
                    src="/schools.avif"
                    alt="Space Explorer"
                    className="w-full h-auto rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-2 rounded-full font-bold text-lg shadow-lg">
                    School Portal
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Section */}
        <div
          className={`w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 transition-all duration-500 overflow-y-auto ${
            activeTab === "school" ? "order-1" : "order-2"
          }`}
        >
          <div className="w-full max-w-sm space-y-6 py-8">
            {/* Tab Switch */}
            <div className="flex gap-3 bg-white/30 backdrop-blur-sm p-1 rounded-xl border border-white/60 shadow-sm">
              <button
                type="button"
                onClick={() => setActiveTab("student")}
                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === "student"
                    ? "bg-blue-700 text-white shadow-lg"
                    : "text-slate-700 hover:bg-white/20"
                }`}
              >
                Student
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("school")}
                className={`flex-1 py-2.5 px-4 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === "school"
                    ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg"
                    : "text-slate-700 hover:bg-white/20"
                }`}
              >
                School
              </button>
            </div>

            {/* Header */}
            <div className="text-center space-y-2">
              <h1 className="text-3xl font-bold text-slate-900">Create Account</h1>
              <p className="text-slate-600">Join EarlyScape as a {activeTab}</p>
            </div>

            {/* Glassy Card */}
            <div className="backdrop-blur-xl bg-white/40 border border-white/60 rounded-2xl p-8 shadow-2xl">
              {success && (
                <div className="mb-6 p-4 bg-green-50/80 backdrop-blur-sm border border-green-200/50 rounded-lg flex items-center gap-3 animate-in">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-green-800 font-medium">Account created!</span>
                </div>
              )}

              {error && !success && (
                <div className="mb-6 p-4 bg-red-50/80 backdrop-blur-sm border border-red-200/50 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span className="text-red-800 font-medium">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">First Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        name="firstName"
                        placeholder="John"
                        value={formData.firstName}
                        onChange={handleChange}
                        disabled={isLoading || success}
                        className="w-full pl-10 pr-4 py-2.5 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-blue-700 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Last Name</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        name="lastName"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={handleChange}
                        disabled={isLoading || success}
                        className="w-full pl-10 pr-4 py-2.5 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-blue-700 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="email"
                      name="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={isLoading || success}
                      className="w-full pl-10 pr-4 py-2.5 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-blue-700 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      disabled={isLoading || success}
                      className="w-full pl-10 pr-12 py-2.5 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-blue-700 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Confirm Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type={showConfirm ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      disabled={isLoading || success}
                      className="w-full pl-10 pr-12 py-2.5 bg-white/60 border border-white/80 rounded-lg backdrop-blur-sm focus:bg-white/90 focus:border-blue-700 focus:outline-none transition-all text-slate-900 placeholder:text-slate-400 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Terms Checkbox */}
                <div className="flex items-center gap-3 py-2">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={isLoading || success}
                    className="w-4 h-4 rounded border-white/60 bg-white/60 cursor-pointer accent-blue-700"
                  />
                  <label htmlFor="terms" className="text-sm text-slate-600">
                    I agree to the{" "}
                    <a href="#" className="text-blue-600 hover:text-blue-700 font-semibold">
                      Terms and Conditions
                    </a>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || success}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all shadow-lg mt-6 ${
                    activeTab === "student"
                      ? "bg-blue-700 hover:bg-blue-600"
                      : "bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600"
                  } disabled:opacity-50`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating Account...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      Create Account
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </button>
              </form>

              {/* Divider */}
              <div className="my-6 flex items-center gap-3">
                <div className="flex-1 h-px bg-white/40"></div>
                <span className="text-xs text-slate-500">Already registered?</span>
                <div className="flex-1 h-px bg-white/40"></div>
              </div>

              {/* Sign In Link */}
              <button
                type="button"
                onClick={() => navigate("/signin")}
                className="block w-full py-3 px-4 bg-white/30 hover:bg-white/50 border border-white/60 rounded-lg text-slate-700 font-semibold transition-all text-center"
              >
                Sign In Instead
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default SignupPage;